// App account (Neon Auth, e-mail + password; registration is closed).
//
// Same-origin calls to the server's /api/auth/* proxy. Session cookies are HttpOnly and
// first-party: nothing is stored in the browser. App start uses GET /api/me (cheap, cached);
// getSession() is the authoritative check against Neon Auth.
//
// Endpoints:
//   POST /api/auth/sign-in/email          { email, password, rememberMe }
//   POST /api/auth/sign-out               {}
//   GET  /api/auth/get-session            -> { user, session } | null
//   POST /api/auth/request-password-reset { email }  (server sets redirectTo; always "success"; ~3/min -> 429)
//   POST /api/auth/reset-password         { token, newPassword }  (400 INVALID_TOKEN / PASSWORD_TOO_SHORT)
//   POST /api/auth/change-password        { currentPassword, newPassword, revokeOtherSessions }  (400 INVALID_PASSWORD)
// The e-mail link opens /reset-password?token=… or /reset-password?error=INVALID_TOKEN (see route.ts);
// links expire after 15 minutes. A reset does not sign the user in.

import type { AppUser } from '../../shared/api';

/** Normalised error codes the UI reacts to. */
export type AuthErrorCode =
  | 'invalid_credentials'
  | 'invalid_email'
  | 'email_not_verified'
  | 'invalid_token'
  | 'password_too_short'
  | 'password_too_long'
  | 'rate_limited'
  | 'forbidden'
  /** The browser could not reach our server. */
  | 'network'
  /** Our server could not reach the auth service (502/503/504, NETWORK_*). */
  | 'unavailable'
  | 'unknown';

/** Minimum password length of Neon Auth (Better Auth default). */
export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 128;
/** Validity of the password e-mail link. */
export const RESET_LINK_MINUTES = 15;

export class AuthError extends Error {
  readonly code: AuthErrorCode;
  /** Code as sent by the server, e.g. "INVALID_EMAIL_OR_PASSWORD" or "HTTP_500". */
  readonly upstreamCode: string;
  /** HTTP status (0 for network errors). */
  readonly status: number;

  constructor(code: AuthErrorCode, message: string, options: { upstreamCode?: string; status?: number } = {}) {
    super(message);
    this.name = 'AuthError';
    this.code = code;
    this.upstreamCode = options.upstreamCode ?? code;
    this.status = options.status ?? 0;
  }
}

export interface Session {
  user: AppUser;
}

/** Account details for the account page (from get-session). */
export interface Account {
  user: AppUser;
  /** ISO timestamp of the registration, if the auth service sends it. */
  createdAt?: string;
}

/**
 * Upstream codes seen in practice: INVALID_EMAIL_OR_PASSWORD (401), INVALID_EMAIL /
 * VALIDATION_ERROR (400), EMAIL_NOT_VERIFIED (403), INVALID_TOKEN, PASSWORD_TOO_SHORT,
 * PASSWORD_TOO_LONG (400), INVALID_ORIGIN (403), NETWORK_* (502, auth service unreachable).
 */
export function normalizeAuthError(upstreamCode: string, status: number): AuthErrorCode {
  const code = upstreamCode.toUpperCase();
  if (status === 429 || code.includes('RATE_LIMIT') || code === 'TOO_MANY_REQUESTS') return 'rate_limited';
  if (code.includes('INVALID_EMAIL_OR_PASSWORD') || code === 'INVALID_PASSWORD' || code === 'USER_NOT_FOUND' || code === 'CREDENTIAL_ACCOUNT_NOT_FOUND') {
    return 'invalid_credentials';
  }
  if (code.includes('TOKEN')) return 'invalid_token';
  if (code === 'PASSWORD_TOO_SHORT') return 'password_too_short';
  if (code === 'PASSWORD_TOO_LONG') return 'password_too_long';
  if (code === 'EMAIL_NOT_VERIFIED') return 'email_not_verified';
  if (code === 'INVALID_EMAIL' || code === 'VALIDATION_ERROR') return 'invalid_email';
  if (code.startsWith('NETWORK') || status === 502 || status === 503 || status === 504) return 'unavailable';
  if (status === 401) return 'invalid_credentials';
  if (status === 403) return 'forbidden';
  return 'unknown';
}

type Json = Record<string, unknown> | null;

async function call<T>(path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api/auth/${path}`, {
      method: body === undefined ? 'GET' : 'POST',
      credentials: 'same-origin',
      headers:
        body === undefined
          ? { Accept: 'application/json' }
          : { Accept: 'application/json', 'Content-Type': 'application/json' },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch (error) {
    throw new AuthError('network', error instanceof Error ? error.message : 'Network error');
  }
  const data = (await res.json().catch(() => null)) as Json;
  if (!res.ok) {
    // Neon Auth: { message, code }; toolkit network errors: { error, code }; our proxy: { error: { code, message } }.
    const nested = data && typeof data.error === 'object' && data.error ? (data.error as Json) : null;
    const upstream = String(data?.code ?? nested?.code ?? `HTTP_${res.status}`);
    const message =
      String(data?.message ?? nested?.message ?? (typeof data?.error === 'string' ? data.error : '')) ||
      res.statusText ||
      `HTTP ${res.status}`;
    throw new AuthError(normalizeAuthError(upstream, res.status), message, { upstreamCode: upstream, status: res.status });
  }
  return data as T;
}

function toAppUser(value: unknown, fallbackEmail = ''): AppUser {
  const u = (value && typeof value === 'object' ? value : {}) as Record<string, unknown>;
  const name = typeof u.name === 'string' && u.name ? u.name : undefined;
  return {
    id: typeof u.id === 'string' ? u.id : '',
    email: typeof u.email === 'string' ? u.email : fallbackEmail,
    ...(name ? { name } : {}),
  };
}

/** Current session or null (also null when the auth service cannot be reached). */
export async function getSession(): Promise<Session | null> {
  try {
    const data = await call<{ user?: unknown } | null>('get-session');
    return data?.user ? { user: toAppUser(data.user) } : null;
  } catch {
    return null;
  }
}

/** Account details; null when signed out or the auth service cannot be reached. */
export async function getAccount(): Promise<Account | null> {
  try {
    const data = await call<{ user?: Record<string, unknown> } | null>('get-session');
    if (!data?.user) return null;
    const createdAt = typeof data.user.createdAt === 'string' ? data.user.createdAt : undefined;
    return { user: toAppUser(data.user), ...(createdAt ? { createdAt } : {}) };
  } catch {
    return null;
  }
}

/** Throws AuthError('invalid_credentials') on a wrong e-mail/password. */
export async function signIn(email: string, password: string): Promise<Session> {
  const data = await call<{ user?: unknown } | null>('sign-in/email', { email, password, rememberMe: true });
  return { user: toAppUser(data?.user, email) };
}

export async function signOut(): Promise<void> {
  await call('sign-out', {});
}

/** Sends the "set a new password" e-mail. Resolves for every well-formed address (no user enumeration). */
export async function requestPasswordReset(email: string): Promise<void> {
  await call('request-password-reset', { email });
}

/** Sets a new password with the token from the e-mail link. Does not sign the user in. */
export async function resetPassword(token: string, newPassword: string): Promise<void> {
  await call('reset-password', { token, newPassword });
}

/**
 * Changes the password of the signed-in user. Throws AuthError('invalid_credentials') on a
 * wrong current password. With `revokeOtherSessions` all other devices are signed out; this
 * device gets a new session cookie.
 */
export async function changePassword(currentPassword: string, newPassword: string, revokeOtherSessions: boolean): Promise<void> {
  await call('change-password', { currentPassword, newPassword, revokeOtherSessions });
}

// ---------------------------------------------------------------------------
// User-facing texts

export type AuthAction = 'sign-in' | 'sign-out' | 'request-reset' | 'reset' | 'change-password';

/** Friendly German message for a failed auth call. */
export function authErrorMessage(error: unknown, action: AuthAction): string {
  const code: AuthErrorCode = error instanceof AuthError ? error.code : 'unknown';
  switch (code) {
    case 'invalid_credentials':
      if (action === 'sign-in') return 'E-Mail-Adresse oder Passwort ist falsch.';
      if (action === 'change-password') return 'Das aktuelle Passwort ist falsch.';
      return 'Das hat nicht geklappt. Bitte versuche es erneut.';
    case 'invalid_email':
      return 'Bitte gib eine gültige E-Mail-Adresse ein.';
    case 'email_not_verified':
      return 'Deine E-Mail-Adresse ist noch nicht bestätigt. Lege dein Passwort über „Passwort vergessen?“ fest – damit wird sie bestätigt.';
    case 'invalid_token':
      return `Der Link ist ungültig oder abgelaufen (er gilt ${RESET_LINK_MINUTES} Minuten und nur einmal). Fordere bitte einen neuen an.`;
    case 'password_too_short':
      return `Das Passwort muss mindestens ${PASSWORD_MIN_LENGTH} Zeichen lang sein.`;
    case 'password_too_long':
      return `Das Passwort darf höchstens ${PASSWORD_MAX_LENGTH} Zeichen lang sein.`;
    case 'rate_limited':
      return action === 'request-reset'
        ? 'Zu viele Anfragen. Bitte warte eine Minute und versuche es dann erneut.'
        : 'Zu viele Versuche. Bitte warte eine Minute und versuche es dann erneut.';
    case 'forbidden':
      return 'Die Anfrage wurde aus Sicherheitsgründen abgelehnt. Lade die Seite neu und versuche es erneut.';
    case 'network':
      return 'Der Server ist nicht erreichbar. Prüfe deine Internetverbindung.';
    case 'unavailable':
      return 'Der Anmeldedienst ist gerade nicht erreichbar. Bitte versuche es in ein paar Minuten erneut.';
    default:
      switch (action) {
        case 'sign-in':
          return 'Die Anmeldung hat nicht geklappt. Bitte versuche es erneut.';
        case 'sign-out':
          return 'Abmelden hat nicht geklappt. Bitte versuche es erneut.';
        case 'request-reset':
          return 'Der Link konnte nicht gesendet werden. Bitte versuche es erneut.';
        case 'reset':
        case 'change-password':
          return 'Das Passwort konnte nicht gespeichert werden. Bitte versuche es erneut.';
      }
  }
}
